import React from "react";
import { makeStyles, withStyles } from "@mui/styles";

import { Grid, Link } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";

import CodeCard from "../../../components/shared/CodeCard.js";

const MyAppLink = React.forwardRef((props, ref) => (
  <RouterLink innerRef={ref} {...props} />
));

const useStyles = makeStyles({
  firstHeader: {
    marginTop: "10px",
  },
});

const StyledListItem = withStyles({
  gutters: {
    padding: 0,
  },
})(ListItem);

const ListNumber = withStyles({
  root: {
    minWidth: "2.0em",
    marginTop: "0.45em",
    fontSize: "1.0em",
  },
})(Typography);

const StyledListItemText = withStyles({
  root: {
    fontSize: "18px",
    fontWeight: 300,
    marginTop: "8px",
  },
  primary: {
    fontFamily: "futura-pt, sans-serif",
    fontSize: "18px",
    fontWeight: 300,
    lineHeight: 1.3,
  },
})(ListItemText);

export function Authentication() {
  const classes = useStyles();

  return (
    <Grid container>
      <Grid item md={8}>
        <h2 className={classes.firstHeader}>Authentication</h2>
        <p>
          <span style={{ fontWeight: "400" }}>Note: </span>
          This document refers to the v2 API for SOS Inventory. This API
          supports all of the transactions available in the application.
        </p>
        <p>
          The rest of this section makes reference to your{" "}
          <code>client ID</code> and <code>client secret</code>. You can find
          the list of your registered applications, along with their client IDs
          and secrets, in the{" "}
          <Link to="/myapps" component={MyAppLink} underline="hover">
            MY APPS
          </Link>{" "}
          section of this Developer Portal.
        </p>
        <List>
          <ListEntry id="1.">
            Have the user authorize your client id. Direct the user's browser to
            <CodeCard>
              https://api.sosinventory.com/oauth2/authorize?response_type=code&client_id=[client
              id]&redirect_uri=[redirect uri]
            </CodeCard>
            <p>
              where the redirect URI is a secure URL (https). If the redirect
              URI is left blank, it will default to the SOS Inventory app. Do
              not leave the square braces around the URI. An example of this
              option is <code>redirect_uri=https://live.sosinventory.com/</code>
              .
            </p>
            <p>
              <span style={{ fontWeight: "bold" }}>
                The API's access privileges will match those of the user
                authorizing the request.
              </span>{" "}
              If you wish to limit the API access, create an SOS user account
              with the API permissions you are willing to grant. This account
              should be utilized whenever you authorize access to the API.
            </p>
            <p>
              After completing the authorization process, SOS will redirect the
              user to the optional redirect URL. It contains a query string with
              the authorization code for obtaining an access token. This
              authorization code can be used only once and is only valid for 24
              hours.
            </p>
          </ListEntry>
          <ListEntry id="2.">
            Make an API call to convert your authorization code into an access
            token.
            <CodeCard>POST https://api.sosinventory.com/oauth2/token</CodeCard>
            <p>Your request should have a header containing:</p>
            <CodeCard>
              Content-Type: application/x-www-form-urlencoded
              <br />
              Host: api.sosinventory.com
            </CodeCard>
            <p>
              The body of the request should be on one line with no extra lines
              at the end and contain the following data:
            </p>
            <CodeCard>
              <pre>
                grant_type=authorization_code
                <br /> &client_id=[client id]
                <br /> &client_secret=[client secret]
                <br /> &code=[authorization code]
                <br /> &redirect_uri=[redirect uri from authorization call]
              </pre>
            </CodeCard>
            <p>The response body will be JSON encoded like:</p>
            <CodeCard>
              <pre>
                {"{"}
                <br /> "access_token": "JdqRERTTeJ86Cisg...",
                <br /> "token_type": "bearer",
                <br /> "expires_in": 7775999,
                <br /> "refresh_token": "vk1yx21bf0ZgvLJj7G..."
                <br />
                {"}"}
              </pre>
            </CodeCard>
            <p>
              The access token is good for 90 days. Before it expires, use the
              refresh token to obtain new access and refresh tokens (see #4
              below).
            </p>
          </ListEntry>
          <ListEntry id="3.">
            Make a test API call to retrieve all the items from the account.
            <CodeCard>GET https://api.sosinventory.com/api/v2/item</CodeCard>
            <p>Your request should have the following header items:</p>
            <CodeCard>
              Content-Type: application/x-www-form-urlencoded
              <br />
              Host: api.sosinventory.com
              <br />
              Authorization: Bearer [access_token]
            </CodeCard>
          </ListEntry>
          <ListEntry id="4.">
            To use the refresh_token to obtain a new access token, make the
            following API call:
            <CodeCard>POST https://api.sosinventory.com/oauth2/token</CodeCard>
            <p>Your request should have a header containing:</p>
            <CodeCard>
              Content-Type: application/x-www-form-urlencoded
              <br />
              Host: api.sosinventory.com
            </CodeCard>
            <p>and a body with the following data:</p>
            <CodeCard>
              grant_type=refresh_token
              <br />
              &refresh_token=[refresh_token]
            </CodeCard>
            <p>
              The response body will be JSON encoded, with a new access token
              and refresh token:
            </p>
            <CodeCard>
              <pre>
                {"{"}
                <br /> "access_token": "b7d902daf4...",
                <br /> "token_type": "bearer",
                <br /> "expires_in": 7775999,
                <br /> "refresh_token": "a814b9d7e5d241af..."
                <br />
                {"}"}
              </pre>
            </CodeCard>
          </ListEntry>
        </List>
      </Grid>
    </Grid>
  );
}

function ListEntry(props) {
  return (
    <StyledListItem alignItems="flex-start">
      <ListNumber variant="body2">{props.id}</ListNumber>
      <StyledListItemText>{props.children}</StyledListItemText>
    </StyledListItem>
  );
}
